import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import { useToast } from '@/components/ui/useToast';
import { useFieldErrors } from '@/lib/useFieldErrors';
import { apiError } from '@/lib/apiError';
import { createRole, listRoles, updateRole } from '@/api/admin';

interface Props {
  open: boolean;
  roleId: number | null;
  onClose: () => void;
}

export function RoleFormModal({ open, roleId, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fe = useFieldErrors<'name' | 'code'>();
  const isEdit = roleId !== null;
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [level, setLevel] = useState('1');
  const [color, setColor] = useState('');

  const rolesQ = useQuery({
    queryKey: ['admin-roles'],
    queryFn: listRoles,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    if (isEdit && rolesQ.data) {
      const r = rolesQ.data.find((x) => x.id === roleId);
      if (r) {
        setCode(r.code);
        setName(r.name);
        setDescription(r.description ?? '');
        setLevel(String(r.level));
        setColor(r.color ?? '');
      }
    } else {
      setCode('');
      setName('');
      setDescription('');
      setLevel('1');
      setColor('');
    }
  }, [open, isEdit, roleId, rolesQ.data]);

  const onErr = (e: unknown) => {
    toast.push(apiError(e, 'Failed'), 'error');
  };

  const createMut = useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      toast.push('Role created', 'success');
      qc.invalidateQueries({ queryKey: ['admin-roles'] });
      qc.invalidateQueries({ queryKey: ['perm-matrix'] });
      onClose();
    },
    onError: onErr,
  });
  const updateMut = useMutation({
    mutationFn: (payload: Parameters<typeof updateRole>[1]) =>
      updateRole(roleId as number, payload),
    onSuccess: () => {
      toast.push('Role updated', 'success');
      qc.invalidateQueries({ queryKey: ['admin-roles'] });
      onClose();
    },
    onError: onErr,
  });

  function submit() {
    if (!fe.validate({
      name: !name.trim() && 'Name required',
      code: !isEdit && !/^[A-Z0-9_]+$/.test(code) && 'Code must be UPPER_SNAKE_CASE',
    })) return;
    if (!isEdit) {
      createMut.mutate({
        code,
        name,
        description: description || undefined,
        level: Number(level),
        color: color || undefined,
      });
    } else {
      updateMut.mutate({
        name,
        description: description || undefined,
        level: Number(level),
        color: color || undefined,
      });
    }
  }

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit Role' : 'New Role'}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={createMut.isPending || updateMut.isPending}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3">
        <FormGroup label="Code * (UPPER_SNAKE_CASE)" error={fe.errors.code}>
          <Input
            invalid={fe.has('code')}
            value={code}
            disabled={isEdit}
            placeholder="e.g. WAREHOUSE_LEAD"
            onChange={(e) => { setCode(e.target.value.toUpperCase()); fe.clear('code'); }}
          />
        </FormGroup>
        <FormGroup label="Name *" error={fe.errors.name}>
          <Input invalid={fe.has('name')} value={name} onChange={(e) => { setName(e.target.value); fe.clear('name'); }} />
        </FormGroup>
        <FormGroup label="Description">
          <Textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormGroup>
        <div className="grid grid-cols-2 gap-3">
          <FormGroup label="Level (higher = more access)">
            <Input
              type="number"
              min="1"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
            />
          </FormGroup>
          <FormGroup label="Badge Color (hex)">
            <Input
              placeholder="#FF8C00"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </FormGroup>
        </div>
      </div>
    </Modal>
  );
}
